import { ReactElement, useCallback, useContext } from "react";
import { IconButton, MenuButton, Text } from "@chakra-ui/react";
import { ContextMenu } from "src/components/common/context-menu/context-menu";
import { FeedCardLayout } from "src/components/feed-card/common/feed-card-layout";
import { FeedCardParsedText } from "src/components/feed-card/common/feed-card-parsed-text";
import { FeedCardContext } from "src/components/feed-card/feed-card";
import { EnumFeedCardBlockType } from "src/components/feed-card/types/enums";
import { IFeedCardHeader, TFeedCardTaggedEntity } from "src/components/feed-card/types/feed-card";
import { IconThreeDots } from "src/components/icons";
import { EnumFeedClickTrackingArea, useFeedClickTracking } from "src/components/pages/feed/hooks/use-feed-click-tracking";
import { useCreateContextMenuProps } from "./context-menu/use-create-context-menu-props";
import { FEED_CARD_CONTEXT_MENU_BUTTON_TEST_ID } from "./feed-card-header.test-ids";

interface IFeedCardHeadingProps extends IFeedCardHeader {
    showContextMenu?: boolean;
    contextMenuItems?: ReadonlyArray<ReactElement>;
    taggedEntities: ReadonlyArray<TFeedCardTaggedEntity> | null;
}

export const FeedCardHeader = ({ text, args, showContextMenu, contextMenuItems, taggedEntities }: IFeedCardHeadingProps) => {
    const context = useContext(FeedCardContext);
    const { trackFeedClick } = useFeedClickTracking();

    const handleLinkClick = useCallback(
        (url: string) => {
            trackFeedClick(context.contentId, url, EnumFeedClickTrackingArea.Header);
        },
        [context.contentId, trackFeedClick]
    );

    const { createdShowContextMenu, createdContextMenuItems } = useCreateContextMenuProps({
        showContextMenu,
        contextMenuItems,
        taggedEntities,
    });

    return (
        <FeedCardLayout.Header isContextMenuVisible={createdShowContextMenu}>
            <Text py={1}>
                {args ? (
                    <FeedCardParsedText
                        text={text}
                        args={args}
                        allowedContentBlocks={[EnumFeedCardBlockType.Link, EnumFeedCardBlockType.Number]}
                        onLinkClick={handleLinkClick}
                    />
                ) : (
                    text
                )}
            </Text>
            {createdShowContextMenu && createdContextMenuItems?.length && (
                <ContextMenu items={createdContextMenuItems} placement="bottom-end">
                    <MenuButton
                        as={IconButton}
                        size="xs"
                        variant="ghost"
                        _focus={{ bg: undefined }}
                        data-test-id={FEED_CARD_CONTEXT_MENU_BUTTON_TEST_ID}
                    >
                        <IconThreeDots />
                    </MenuButton>
                </ContextMenu>
            )}
        </FeedCardLayout.Header>
    );
};
